import store from 'src/store';
import { selectModule } from 'src/store/selectors/configurations';
import { isEmpty, isNil, lensPath, view } from 'ramda';
import { SETTINGS_SUBPAGE_URL_REGEX } from 'src/constants/regExps';
import { I18n } from 'src/translations/i18n';
import { routes as allRoutes, homeRoute } from './routes';
import { FlagFrom } from './interfaces';
class RoutingHelper {
    static routesIdMap = this.getRoutesIdMap();
    static routesUrlMap = this.getRoutesUrlMap();
    static checkUserRights;
    static isModuleEnabled;
    static getRoutesIdMap(routes = [homeRoute, ...allRoutes], parentPath = '') {
        return routes.reduce((acc, route) => {
            const currentPath = parentPath + route.link;
            acc[route.selectedTabId] = {
                ...route,
                link: currentPath
            };
            if (route.subPages) {
                acc = {
                    ...acc,
                    ...this.getRoutesIdMap(route.subPages, currentPath)
                };
            }
            return acc;
        }, {});
    }
    static getRoutesUrlMap() {
        return Object.values(this.routesIdMap).reduce((acc, route) => {
            acc[route.link] = route;
            return acc;
        }, {});
    }
    static recomputeRoutes() {
        this.routesIdMap = this.getRoutesIdMap();
        this.routesUrlMap = this.getRoutesUrlMap();
    }
    static getRouteByPath(path) {
        const route = this.routesUrlMap[path];
        return route ? this.getFullRoute(route) : null;
    }
    static getAllowedRoutes(routes = [], isModuleEnabled, checkUserRights) {
        this.checkUserRights = checkUserRights;
        this.isModuleEnabled = isModuleEnabled;
        const fullRouteObjects = this.getRoutes(routes);
        const pluckAllowedRoutes = (reducedRoutes) => {
            return reducedRoutes.reduce((menuRoutes, route) => {
                const routeCopy = { ...route };
                if (route.subPages)
                    routeCopy.subPages = pluckAllowedRoutes(route.subPages);
                const isAllowed = this.isRouteAllowed(routeCopy);
                return isAllowed ? [...menuRoutes, routeCopy] : menuRoutes;
            }, []);
        };
        return pluckAllowedRoutes(fullRouteObjects);
    }
    static getRoutes(routes = []) {
        return routes.map(this.getFullRoute.bind(this));
    }
    static getFullRoute(route) {
        const selectedRoute = this.routesIdMap[route.selectedTabId];
        const { selectedTabId, nameKey, description, subPages } = selectedRoute;
        return {
            ...selectedRoute,
            name: I18n.t(nameKey || selectedTabId),
            description: I18n.t(description || `${selectedTabId}Description`),
            subPages: subPages?.map(this.getFullRoute.bind(this))
        };
    }
    static isPathAllowed(url, checkUserRights, isModuleEnabled) {
        this.checkUserRights = checkUserRights;
        this.isModuleEnabled = isModuleEnabled;
        const route = this.routesUrlMap[url];
        if (!route)
            return false;
        return this.isRouteAllowed(route);
    }
    static isRouteAllowed(route) {
        if (SETTINGS_SUBPAGE_URL_REGEX.test(route.link)) {
            return this.checkSettingsRouteAllowed(route);
        }
        else {
            return this.checkCommonRouteAllowed(route);
        }
    }
    static checkSettingsRouteAllowed(route) {
        const commonConditions = this.checkCommonRouteAllowed(route);
        const isLimited = this.checkIsLimitedSettingsEnabled(route);
        return commonConditions && !isLimited;
    }
    static checkCommonRouteAllowed(route) {
        const { moduleFlag, moduleFlagSomeOf, moduleFlagEveryOf, rightFlag, flagFrom = FlagFrom.Read, accessController } = route;
        const isUserHaveRights = !rightFlag || this.checkUserRights(rightFlag, flagFrom, flagFrom === FlagFrom.Visible);
        const isEnabled = this.isAllowedByModuleStatus(moduleFlag, moduleFlagSomeOf, moduleFlagEveryOf);
        const isAllowedByModuleConfigs = this.isAllowedByModuleConfigDependencies(route);
        const isAllowedByAccessController = accessController?.(this.checkUserRights) ?? true;
        return isEnabled && isUserHaveRights && isAllowedByModuleConfigs && isAllowedByAccessController;
    }
    static isAllowedByModuleStatus(moduleFlag, moduleFlagSomeOf, moduleFlagEveryOf) {
        if (!isNil(moduleFlag)) {
            return this.isModuleEnabled(moduleFlag);
        }
        if (moduleFlagSomeOf?.length) {
            return moduleFlagSomeOf.some((flag) => this.isModuleEnabled(flag));
        }
        if (moduleFlagEveryOf?.length) {
            return moduleFlagEveryOf.every((flag) => this.isModuleEnabled(flag));
        }
        return true;
    }
    static isAllowedByModuleConfigDependencies(route) {
        return route.moduleConfigDependencies
            ? this.checkModuleConfigDependencies(route.moduleConfigDependencies)
            : true;
    }
    static checkModuleConfigDependencies(moduleConfigDependencies) {
        const state = store.getState();
        return moduleConfigDependencies.every((moduleDependency) => {
            const module = selectModule(state, moduleDependency.moduleFlag);
            return this.checkModuleDependency(moduleDependency, module);
        });
    }
    static checkModuleDependency(moduleDependency, module) {
        if (!module?.Enabled || isEmpty(module?.Configuration))
            return false;
        const path = lensPath([...moduleDependency.propertyPath.split('.')]);
        return view(path, module.Configuration);
    }
    static checkIsLimitedSettingsEnabled(route) {
        const isWriteAllowed = this.checkUserRights(route.rightFlag, FlagFrom.Write);
        return isWriteAllowed ? false : this.checkUserRights('limitedSettings', FlagFrom.Visible, true);
    }
}
export default RoutingHelper;
